/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useReducer } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Link, Typography2 } from '@coursera/cds-core';

import { useSkillAdvancementData } from 'bundles/skills-common/private/skill-advancement/useSkillAdvancementData';

import _t from 'i18n!nls/skills-common';

import SkillAdvancementCard from './SkillAdvancementCard';

type Props = {
  /** Optional CSS class name for styling */
  className?: string;
  /** The ID of the course being tracked */
  courseId: string;
  /** Flag indicating if this is a specialization context */
  isS12n: boolean;
  /** The ID of the user whose skills are being tracked */
  userId: number;
};

const styles = {
  container: css`
    padding: var(--cds-spacing-200);
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
  `,
};

/**
 * SkillAdvancementContainer displays a user's skill advancement progress
 *
 * This component shows skill advancement cards for skills that have improved,
 * with options to show more or fewer skills. It integrates with the program's
 * skillset system and provides different displays based on whether the context
 * is a specialization or not.
 *
 * @component
 * @param {string} [props.className] - Optional CSS class name for styling
 * @param {string} props.courseId - The ID of the course being tracked
 * @param {boolean} props.isS12n - Flag indicating if this is a specialization context
 * @param {number} props.userId - The ID of the user whose skills are being tracked
 *
 * @returns {JSX.Element|null} The rendered component or null if there are no advancements,
 *                            no program slug, or if there's an error
 *
 */
const SkillAdvancementContainer = ({ className, isS12n, courseId, userId }: Props) => {
  const [showMore, toggleShowMore] = useReducer((value) => !value, false);
  const { error, advancements, skillset, programSlug } = useSkillAdvancementData({ courseId, userId });

  // Don't show if:
  //    there are no advancements
  //    the user is not part of a program (won't have access to skills)
  //    there's been an error
  if (!advancements?.length || !programSlug || error) {
    return null;
  }

  return (
    <div className={className} css={styles.container}>
      <Typography2 component="h2" variant="subtitleLarge">
        {_t('You increased your skill scores!')}
      </Typography2>
      {!isS12n && skillset && (
        <Typography2 component="p" variant="bodySecondary">
          <FormattedMessage
            message={_t('You’re making great progress in the {skillset}')}
            skillset={
              <Link variant="standard" href={`/programs/${programSlug}/skillsets/${skillset.slug}`} target="_blank">
                {_t('#{skillsetName} SkillSet', {
                  skillsetName: skillset.name,
                })}
              </Link>
            }
          />
        </Typography2>
      )}
      {advancements.slice(0, showMore ? 5 : 1).map((advancement) => (
        <SkillAdvancementCard
          key={advancement.skillId}
          skill={{
            ...advancement,
            // is in a specialization, or there isn't a skillset, or if the skill is not found, don't show a targetScore
            targetScore:
              (!isS12n && skillset?.skills.find((skill) => skill.skillId === advancement.skillId)?.targetScore) ||
              undefined,
          }}
          programSlug={programSlug}
          showSkillPageLink={!isS12n && !skillset}
        />
      ))}
      <div>
        <Button edgeAlign="start" variant="ghost" onClick={toggleShowMore}>
          {showMore ? _t('Show less skills') : _t('Show more skills')}
        </Button>
      </div>
    </div>
  );
};

export default SkillAdvancementContainer;
